import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  GlobeAltIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  SparklesIcon,
  ClockIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CloudIcon,
  UserIcon,
  PhoneIcon,
  MapPinIcon,
  PlusIcon,
  XMarkIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import onboardingService from '../services/onboardingService';
import toast from 'react-hot-toast';

const Onboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [countries, setCountries] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [showCustomFieldModal, setShowCustomFieldModal] = useState(false);
  const [customFieldForm, setCustomFieldForm] = useState({
    name: '',
    type: 'text',
    module: 'lead',
    required: false,
    options: []
  });

  const [formData, setFormData] = useState({
    // Company Info
    companyName: '',
    companyEmail: '',
    phone: '',
    country: '',
    industry: '',

    // Custom Fields
    customFields: {},
    selectedCustomFields: [],

    // User Info
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const plansContainerRef = useRef(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load countries and industries in parallel
      const [countriesResult, industriesResult] = await Promise.all([
        onboardingService.getCountries(),
        onboardingService.getIndustries()
      ]);

      if (countriesResult.success) {
        setCountries(countriesResult.data);
      }

      if (industriesResult.success) {
        setIndustries(industriesResult.data);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  const loadPlansForCountry = async (countryId) => {
    if (!countryId) return;

    setLoading(true);
    try {
      const plansResult = await onboardingService.getPlansByCountry(countryId);
      if (plansResult.success) {
        setPlans(plansResult.data);
        // Reset selected plan when country changes
        setSelectedPlan(null);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Failed to load plans for selected country');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomFieldsForIndustry = async (industryId) => {
    if (!industryId) {
      setCustomFields([]);
      return;
    }

    try {
      // Load custom fields for both leads and customers
      const [leadFieldsResult, customerFieldsResult] = await Promise.all([
        onboardingService.getIndustryCustomFields(industryId, 'lead'),
        onboardingService.getIndustryCustomFields(industryId, 'customer')
      ]);

      const leadFields = leadFieldsResult.success ? leadFieldsResult.data.customFields : [];
      const customerFields = customerFieldsResult.success ? customerFieldsResult.data.customFields : [];

      // Combine and deduplicate fields (in case same field exists for both modules)
      const allFields = [...leadFields, ...customerFields];
      const uniqueFields = allFields.filter((field, index, self) =>
        index === self.findIndex(f => f._id === field._id)
      );

      setCustomFields(uniqueFields);
    } catch (error) {
      console.error('Error loading custom fields:', error);
      setCustomFields([]);
      toast.error('Failed to load custom fields for selected industry');
    }
  };

  const scrollPlans = (direction) => {
    if (plansContainerRef.current) {
      const scrollAmount = 320; // Card width + spacing
      const currentScroll = plansContainerRef.current.scrollLeft;
      const newScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      plansContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Company Info
        if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
        if (!formData.companyEmail.trim()) newErrors.companyEmail = 'Company email is required';
        if (!/\S+@\S+\.\S+/.test(formData.companyEmail)) newErrors.companyEmail = 'Valid email is required';
        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
        if (!selectedCountry) newErrors.country = 'Please select your country';
        if (!selectedIndustry) newErrors.industry = 'Please select your industry';

        // Custom field validation happens when using the fields, not during onboarding
        break;

      case 2: // Plan Selection
        if (!selectedCountry) {
          newErrors.country = 'Please select your country first';
        }
        if (!selectedPlan) newErrors.plan = 'Please select a plan';
        break;

      case 3: // User Account
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Valid email is required';
        if (!formData.password) newErrors.password = 'Password is required';
        if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      setErrors({});
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => prev - 1);
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setFormData(prev => ({
      ...prev,
      country: country._id
    }));
    loadPlansForCountry(country._id);

    // Clear error
    if (errors.country) {
      setErrors(prev => ({
        ...prev,
        country: ''
      }));
    }
  };

  const handleIndustrySelect = (industry) => {
    setSelectedIndustry(industry);
    setFormData(prev => ({
      ...prev,
      industry: industry._id
    }));
    loadCustomFieldsForIndustry(industry._id);

    // Clear error
    if (errors.industry) {
      setErrors(prev => ({
        ...prev,
        industry: ''
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setLoading(true);
    try {
      const companyData = {
        companyName: formData.companyName,
        companyEmail: formData.companyEmail,
        phone: formData.phone,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        country: selectedCountry._id,
        industry: selectedIndustry._id,
        plan: selectedPlan._id,
        customFields: formData.selectedCustomFields
      };

      const result = await onboardingService.createCompany(companyData);

      if (result.success) {
        toast.success('Company created successfully! Starting your 2-day trial.');
        // Store token and redirect to dashboard
        localStorage.setItem('token', result.data.token);
        navigate('/dashboard');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { id: 1, name: 'Company', icon: BuildingOfficeIcon },
    { id: 2, name: 'Plan', icon: CreditCardIcon },
    { id: 3, name: 'Account', icon: UserIcon }
  ];

  if (loading && plans.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <LoadingSpinner size="large" text="Loading onboarding..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-indigo-400/30 to-purple-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-br from-blue-400/30 to-cyan-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-pink-400/20 to-rose-500/20 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="relative min-h-screen flex flex-col">
        {/* Enhanced Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-3 md:py-6 px-4"
        >
          <motion.img
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            src="/assets/logo.png"
            alt="Company Logo"
            className="mx-auto h-16 w-16 md:h-24 md:w-24 object-contain rounded-lg mb-2 md:mb-4"
          />
          <h1 className="text-xl md:text-3xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-1 md:mb-2">
            Welcome to InnovativeCRM!
          </h1>
          <p className="text-gray-600 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed px-2">
            Let's set up your company workspace with a <span className="font-semibold text-indigo-600">free 2-day trial</span>.
            Follow these simple steps to get started.
          </p>
        </motion.div>

        {/* Enhanced Progress Steps */}
        <div className="px-4 mb-4 md:mb-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center space-x-2 md:space-x-4">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex flex-col items-center"
                  >
                    <div className={`w-12 h-12 md:w-16 md:h-16 rounded-2xl md:rounded-3xl flex items-center justify-center border-2 md:border-3 shadow-lg md:shadow-xl transition-all duration-300 ${
                      currentStep >= step.id
                        ? 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 border-indigo-400 text-white shadow-indigo-500/25'
                        : 'bg-white border-gray-200 text-gray-400 shadow-gray-200'
                    }`}>
                      <step.icon className="h-6 w-6 md:h-8 md:w-8" />
                    </div>
                    <p className={`mt-1 md:mt-2 text-xs font-bold tracking-wide ${
                      currentStep >= step.id ? 'text-indigo-600' : 'text-gray-400'
                    }`}>
                      {step.name}
                    </p>
                  </motion.div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 h-1 bg-gray-200 mt-[-12px] md:mt-[-16px] rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: currentStep > step.id ? '100%' : '0%' }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full shadow-lg"
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 px-4 pb-4 md:pb-6">
          <div className="max-w-6xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="bg-white/80 backdrop-blur-xl rounded-xl md:rounded-2xl shadow-2xl border border-white/20 p-4 md:p-6 lg:p-8"
              >
                {currentStep === 1 && (
                  <div className="space-y-4 md:space-y-6 lg:space-y-8">
                    <div className="text-center">
                      <div className="bg-gradient-to-r from-indigo-100 to-purple-100 w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6">
                        <BuildingOfficeIcon className="h-8 w-8 md:h-10 md:w-10 text-indigo-600" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3">Tell us about your company</h2>
                      <p className="text-gray-600 text-base md:text-lg">We'll use this information to set up your workspace</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Company Name *
                        </label>
                        <div className="relative">
                          <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            value={formData.companyName}
                            onChange={(e) => handleInputChange('companyName', e.target.value)}
                            className={`w-full pl-12 pr-4 py-4 bg-gray-50 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-gray-900 placeholder-gray-500 ${
                              errors.companyName ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                            }`}
                            placeholder="Enter your company name"
                          />
                        </div>
                        {errors.companyName && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-2 text-sm text-red-600"
                          >
                            {errors.companyName}
                          </motion.p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Company Email *
                        </label>
                        <div className="relative">
                          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <input
                            type="email"
                            value={formData.companyEmail}
                            onChange={(e) => handleInputChange('companyEmail', e.target.value)}
                            className={`w-full pl-12 pr-4 py-4 bg-gray-50 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-gray-900 placeholder-gray-500 ${
                              errors.companyEmail ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                            }`}
                            placeholder="company@example.com"
                          />
                        </div>
                        {errors.companyEmail && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-2 text-sm text-red-600"
                          >
                            {errors.companyEmail}
                          </motion.p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <div className="relative">
                          <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            className={`w-full pl-12 pr-4 py-4 bg-gray-50 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-gray-900 placeholder-gray-500 ${
                              errors.phone ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                            }`}
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>
                        {errors.phone && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-2 text-sm text-red-600"
                          >
                            {errors.phone}
                          </motion.p>
                        )}
                      </div>
                    </div>

                    {/* Country Selection */}
                    <div>
                      <label className="block text-sm font-bold text-gray-800 mb-3 md:mb-4 text-center">
                        Select Your Country *
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3 max-h-60 md:max-h-80 overflow-y-auto">
                        {countries.map((country) => (
                          <motion.div
                            key={country._id}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleCountrySelect(country)}
                            className={`relative p-2 md:p-3 rounded-xl border-2 cursor-pointer transition-all duration-300 shadow-md hover:shadow-lg backdrop-blur-sm ${
                              selectedCountry?._id === country._id
                                ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 shadow-indigo-500/25'
                                : 'border-gray-200 bg-white/70 hover:border-indigo-300 hover:bg-white/90'
                            }`}
                          >
                            <div className="flex flex-col items-center space-y-1 md:space-y-2">
                              <div className="text-xl md:text-2xl">{country.flag || '🏳️'}</div>
                              <div className="text-center">
                                <p className="font-semibold text-gray-900 text-xs leading-tight">{country.name}</p>
                                <p className="text-xs text-gray-500">{country.code}</p>
                              </div>
                              {selectedCountry?._id === country._id && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="absolute -top-1 -right-1"
                                >
                                  <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full p-0.5 shadow-sm">
                                    <CheckCircleIcon className="h-3 w-3 text-white" />
                                  </div>
                                </motion.div>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                      {errors.country && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-3 md:mt-4 text-sm text-red-600 text-center font-medium"
                        >
                          {errors.country}
                        </motion.p>
                      )}
                    </div>

                    {/* Industry Selection */}
                    <div>
                      <label className="block text-sm font-bold text-gray-800 mb-3 md:mb-4 text-center">
                        Select Your Industry *
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 max-h-60 md:max-h-80 overflow-y-auto">
                        {industries.map((industry) => (
                          <motion.div
                            key={industry._id}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleIndustrySelect(industry)}
                            className={`relative p-3 md:p-4 rounded-2xl border-2 cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl backdrop-blur-sm ${
                              selectedIndustry?._id === industry._id
                                ? 'border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-500/25'
                                : 'border-gray-200 bg-white/60 hover:border-emerald-300 hover:bg-white/80'
                            }`}
                          >
                            <div className="text-center">
                              <div className="text-xl md:text-2xl mb-1 md:mb-2">🏢</div>
                              <p className="font-bold text-gray-900 text-sm">{industry.name}</p>
                              {industry.description && (
                                <p className="text-xs text-gray-600 mt-1 leading-tight">{industry.description}</p>
                              )}
                              {selectedIndustry?._id === industry._id && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="absolute -top-2 -right-2"
                                >
                                  <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full p-1 shadow-lg">
                                    <CheckCircleIcon className="h-4 w-4 text-white" />
                                  </div>
                                </motion.div>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                      {errors.industry && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-3 md:mt-4 text-sm text-red-600 text-center font-medium"
                        >
                          {errors.industry}
                        </motion.p>
                      )}
                    </div>

                    {/* Custom Fields */}
                    {/* Custom Fields Section */}
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-4 md:p-6 border border-purple-100">
                      <div className="flex items-center justify-between mb-4 md:mb-6">
                        <div className="flex items-center space-x-2 md:space-x-3">
                          <div className="p-1 md:p-2 bg-purple-100 rounded-lg">
                            <PlusIcon className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                          </div>
                          <div>
                            <h4 className="text-base md:text-lg font-semibold text-gray-800">Custom Fields</h4>
                            <p className="text-xs md:text-sm text-gray-600">Select fields for your leads and customers</p>
                          </div>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setShowCustomFieldModal(true)}
                          className="flex items-center space-x-2 px-3 py-2 md:px-4 md:py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all duration-200 text-xs md:text-sm font-medium shadow-lg"
                        >
                          <PlusIcon className="h-3 w-3 md:h-4 md:w-4" />
                          <span>Add Field</span>
                        </motion.button>
                      </div>

                      {/* Industry Fields */}
                      {customFields.length > 0 && (
                        <div className="mb-6">
                          <h5 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                            <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                            Available Fields from {selectedIndustry?.name}
                          </h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {customFields.map((field, index) => {
                              const isSelected = formData.selectedCustomFields?.some(f => f.name === field.name) || false;
                              const selectedField = formData.selectedCustomFields?.find(f => f.name === field.name);

                              return (
                                <motion.div
                                  key={`${field.name}-${index}`}
                                  whileHover={{ scale: 1.02 }}
                                  className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                    isSelected
                                      ? 'border-emerald-300 bg-emerald-50 shadow-md'
                                      : 'border-gray-200 bg-white hover:border-emerald-200 hover:shadow-sm'
                                  }`}
                                  onClick={() => {
                                    setFormData(prev => {
                                      const selectedFields = prev.selectedCustomFields || [];
                                      if (isSelected) {
                                        // Remove field
                                        return {
                                          ...prev,
                                          selectedCustomFields: selectedFields.filter(f => f.name !== field.name)
                                        };
                                      } else {
                                        // Add field
                                        return {
                                          ...prev,
                                          selectedCustomFields: [...selectedFields, {
                                            ...field,
                                            required: false
                                          }]
                                        };
                                      }
                                    });
                                  }}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <h6 className="text-sm font-medium text-gray-800 truncate">{field.name}</h6>
                                        {isSelected && (
                                          <div className="flex items-center space-x-1">
                                            <input
                                              type="checkbox"
                                              checked={selectedField?.required || false}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                setFormData(prev => ({
                                                  ...prev,
                                                  selectedCustomFields: prev.selectedCustomFields.map(f =>
                                                    f.name === field.name ? { ...f, required: e.target.checked } : f
                                                  )
                                                }));
                                              }}
                                              className="h-3 w-3 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                            />
                                            <label className="text-xs text-red-600 font-medium">Required</label>
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <span className="text-xs text-gray-500 capitalize bg-gray-100 px-2 py-1 rounded-full">
                                          {field.type}
                                        </span>
                                        <span className="text-xs text-gray-400">•</span>
                                        <span className="text-xs text-gray-500 capitalize bg-gray-100 px-2 py-1 rounded-full">
                                          {field.module}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="ml-2">
                                      {isSelected ? (
                                        <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                                          <CheckCircleIcon className="h-3 w-3 text-white" />
                                        </div>
                                      ) : (
                                        <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                                      )}
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* User Added Fields */}
                      {formData.selectedCustomFields && formData.selectedCustomFields.filter(f => !f.isIndustry).length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                            Your Custom Fields
                          </h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {formData.selectedCustomFields.filter(f => !f.isIndustry).map((field, index) => (
                              <motion.div
                                key={`user-field-${field.name}-${index}`}
                                whileHover={{ scale: 1.02 }}
                                className="relative p-4 rounded-xl border-2 border-blue-200 bg-blue-50 shadow-sm"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2 mb-2">
                                      <h6 className="text-sm font-medium text-gray-800 truncate">{field.name}</h6>
                                      {field.required && (
                                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                                          Required
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center space-x-2 mb-2">
                                      <span className="text-xs text-gray-600 capitalize bg-blue-100 px-2 py-1 rounded-full">
                                        {field.type}
                                      </span>
                                      <span className="text-xs text-gray-400">•</span>
                                      <span className="text-xs text-gray-600 capitalize bg-blue-100 px-2 py-1 rounded-full">
                                        {field.module}
                                      </span>
                                    </div>
                                    {(field.type === 'dropdown' || field.type === 'multiselect') && field.options && field.options.length > 0 && (
                                      <div className="mt-2">
                                        <p className="text-xs text-gray-600 mb-1">Options:</p>
                                        <div className="flex flex-wrap gap-1">
                                          {field.options.map((option, optIndex) => (
                                            <span key={optIndex} className="text-xs bg-white px-2 py-1 rounded border border-blue-200">
                                              {option}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => {
                                      setFormData(prev => ({
                                        ...prev,
                                        selectedCustomFields: prev.selectedCustomFields.filter((_, i) =>
                                          !(prev.selectedCustomFields[i].name === field.name && !prev.selectedCustomFields[i].isIndustry)
                                        )
                                      }));
                                    }}
                                    className="ml-2 p-1 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                  </motion.button>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Summary */}
                      {formData.selectedCustomFields && formData.selectedCustomFields.length > 0 && (
                        <div className="mt-6 p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl border border-emerald-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                                <span className="text-sm font-medium text-gray-700">
                                  {formData.selectedCustomFields.length} field{formData.selectedCustomFields.length !== 1 ? 's' : ''} selected
                                </span>
                              </div>
                              {formData.selectedCustomFields.filter(f => f.required).length > 0 && (
                                <div className="flex items-center space-x-2">
                                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                  <span className="text-sm font-medium text-gray-700">
                                    {formData.selectedCustomFields.filter(f => f.required).length} required
                                  </span>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => setFormData(prev => ({ ...prev, selectedCustomFields: [] }))}
                              className="text-xs text-gray-500 hover:text-gray-700 underline"
                            >
                              Clear all
                            </button>
                          </div>
                        </div>
                      )}

                      {(!customFields || customFields.length === 0) && (!formData.selectedCustomFields || formData.selectedCustomFields.filter(f => !f.isIndustry).length === 0) && (
                        <div className="text-center py-8">
                          <div className="bg-gray-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <PlusIcon className="h-8 w-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Custom Fields</h3>
                          <p className="text-gray-600 text-sm mb-4">Add custom fields to collect additional information from your leads and customers.</p>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowCustomFieldModal(true)}
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all duration-200 text-sm font-medium shadow-lg"
                          >
                            <PlusIcon className="h-4 w-4" />
                            <span>Add Your First Field</span>
                          </motion.button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-4 md:space-y-6 lg:space-y-8">
                    <div className="text-center">
                      <div className="bg-gradient-to-r from-purple-100 to-pink-100 w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6">
                        <CreditCardIcon className="h-8 w-8 md:h-10 md:w-10 text-purple-600" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3">Choose your plan</h2>
                      <p className="text-gray-600 text-base md:text-lg">Select a plan that fits your business needs</p>
                      {selectedCountry && (
                        <div className="inline-flex items-center px-3 py-2 md:px-4 md:py-2 bg-indigo-100 rounded-full mt-3 md:mt-4">
                          <MapPinIcon className="h-3 w-3 md:h-4 md:w-4 text-indigo-600 mr-2" />
                          <span className="text-indigo-700 font-medium text-sm">Plans for {selectedCountry.name}</span>
                        </div>
                      )}
                    </div>

                    {!selectedCountry ? (
                      <div className="text-center py-12">
                        <div className="bg-gray-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <GlobeAltIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Select Your Country First</h3>
                        <p className="text-gray-600">Please go back to the previous step and select your country to see available plans.</p>
                      </div>
                    ) : loading ? (
                      <div className="text-center py-12">
                        <LoadingSpinner size="large" text="Loading plans..." />
                      </div>
                    ) : plans.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="bg-yellow-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <ChartBarIcon className="h-8 w-8 text-yellow-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Plans Available</h3>
                        <p className="text-gray-600">No plans are currently available for {selectedCountry.name}. Please contact support.</p>
                      </div>
                    ) : (
                      <div className="relative">
                        {/* Horizontal Slider Container */}
                        <div className="overflow-x-auto scrollbar-hide">
                          <div className="flex space-x-6 pb-4 px-2 min-w-max" ref={plansContainerRef}>
                            {plans.map((plan) => {
                              const hasDiscount = plan.discount > 0;
                              // Calculate original price if there's a discount
                              const originalPrice = hasDiscount ? plan.price / (1 - plan.discount / 100) : plan.price;

                              return (
                                <motion.div
                                  key={plan._id}
                                  whileHover={{ scale: 1.02, y: -3 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() => setSelectedPlan(plan)}
                                  className={`relative flex-shrink-0 w-72 md:w-80 p-6 md:p-8 rounded-2xl md:rounded-3xl border-3 cursor-pointer transition-all duration-300 shadow-xl hover:shadow-2xl backdrop-blur-sm overflow-hidden ${
                                    selectedPlan?._id === plan._id
                                      ? 'border-purple-500 bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 shadow-purple-500/25'
                                      : 'border-gray-200 bg-white/60 hover:border-purple-300 hover:bg-white/80'
                                  }`}
                                >
                                  {/* Popular Badge */}
                                  {plan.name === 'Professional' && (
                                    <div className="absolute top-4 left-4 z-10">
                                      <div className="bg-gradient-to-r from-orange-400 to-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                        MOST POPULAR
                                      </div>
                                    </div>
                                  )}

                                  {/* Discount Badge */}
                                  {hasDiscount && (
                                    <div className="absolute top-4 right-4 z-10">
                                      <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                        {plan.discount}% OFF
                                      </div>
                                    </div>
                                  )}

                                  {/* Selection Checkmark */}
                                  {selectedPlan?._id === plan._id && (
                                    <motion.div
                                      initial={{ scale: 0, rotate: -180 }}
                                      animate={{ scale: 1, rotate: 0 }}
                                      className="absolute -top-4 -right-4 z-20"
                                    >
                                      <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 rounded-full p-3 shadow-xl">
                                        <CheckCircleIcon className="h-6 w-6 text-white" />
                                      </div>
                                    </motion.div>
                                  )}

                                  <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>

                                    {/* Pricing Section */}
                                    <div className="mb-6">
                                      {hasDiscount ? (
                                        <div className="space-y-3">
                                          <div className="flex items-center justify-center">
                                            <span className="text-2xl font-bold text-gray-400 line-through mr-2">
                                              {plan.currency?.symbol || '$'}{originalPrice.toFixed(2)}
                                            </span>
                                            <span className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                                              {plan.currency?.symbol || '$'}{plan.price}
                                            </span>
                                          </div>
                                          <div className="text-sm text-green-600 font-semibold bg-green-50 px-3 py-1 rounded-full inline-block">
                                            Save {plan.currency?.symbol || '$'}{(originalPrice - plan.price).toFixed(2)} ({plan.discount}%)
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="mb-3">
                                          <span className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                                            {plan.currency?.symbol || '$'}{plan.price}
                                          </span>
                                        </div>
                                      )}

                                      {/* Highlighted Billing Cycle */}
                                      <div className="mt-4 mb-4">
                                        <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold text-sm rounded-full shadow-lg">
                                          <CreditCardIcon className="h-4 w-4 mr-2" />
                                          {plan.billingCycle.toUpperCase()}
                                        </div>
                                      </div>
                                    </div>

                                    <p className="text-gray-600 text-sm leading-relaxed mb-6">{plan.description}</p>
                                  </div>

                                  {/* Features */}
                                  <div className="space-y-3 mb-6">
                                    <div className="flex items-center space-x-3">
                                      <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-2 rounded-lg">
                                        <UserGroupIcon className="h-4 w-4 text-purple-600" />
                                      </div>
                                      <span className="text-sm text-gray-700 font-medium">{plan.maxUsers} users</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                      <div className="bg-gradient-to-r from-blue-100 to-cyan-100 p-2 rounded-lg">
                                        <CloudIcon className="h-4 w-4 text-blue-600" />
                                      </div>
                                      <span className="text-sm text-gray-700 font-medium">{plan.storageLimitMB}MB storage</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                      <div className="bg-gradient-to-r from-green-100 to-emerald-100 p-2 rounded-lg">
                                        <ChartBarIcon className="h-4 w-4 text-green-600" />
                                      </div>
                                      <span className="text-sm text-gray-700 font-medium">{plan.maxLeads} leads</span>
                                    </div>
                                  </div>

                                  {/* Trial Badge */}
                                  <div className="pt-4 border-t border-gray-200">
                                    <div className="flex items-center justify-center space-x-2 text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                                      <ClockIcon className="h-4 w-4" />
                                      <span className="font-medium">2 days free trial</span>
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Navigation Arrows */}
                        <button
                          onClick={() => scrollPlans('left')}
                          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 md:p-3 transition-all duration-200 hover:scale-110 border border-gray-200"
                        >
                          <ArrowLeftIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-600" />
                        </button>
                        <button
                          onClick={() => scrollPlans('right')}
                          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 md:p-3 transition-all duration-200 hover:scale-110 border border-gray-200"
                        >
                          <ArrowRightIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-600" />
                        </button>

                        {/* Scroll Indicators */}
                        <div className="flex justify-center mt-4 md:mt-6 space-x-2">
                          <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 md:px-3 md:py-1 rounded-full">
                            ← Scroll or use arrows to see more plans →
                          </div>
                        </div>
                      </div>
                    )}

                    {errors.plan && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-red-50 border border-red-200 rounded-2xl p-4"
                      >
                        <p className="text-sm text-red-600 text-center">{errors.plan}</p>
                      </motion.div>
                    )}
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-4 md:space-y-6 lg:space-y-8">
                    <div className="text-center">
                      <div className="bg-gradient-to-r from-green-100 to-emerald-100 w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6">
                        <UserIcon className="h-8 w-8 md:h-10 md:w-10 text-green-600" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3">Create your account</h2>
                      <p className="text-gray-600 text-base md:text-lg">You'll be the admin of your company workspace</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          First Name *
                        </label>
                        <div className="relative">
                          <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            className={`w-full pl-12 pr-4 py-4 bg-gray-50 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 text-gray-900 placeholder-gray-500 ${
                              errors.firstName ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                            }`}
                            placeholder="John"
                          />
                        </div>
                        {errors.firstName && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-2 text-sm text-red-600"
                          >
                            {errors.firstName}
                          </motion.p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Last Name *
                        </label>
                        <div className="relative">
                          <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            className={`w-full pl-12 pr-4 py-4 bg-gray-50 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 text-gray-900 placeholder-gray-500 ${
                              errors.lastName ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                            }`}
                            placeholder="Doe"
                          />
                        </div>
                        {errors.lastName && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-2 text-sm text-red-600"
                          >
                            {errors.lastName}
                          </motion.p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className={`w-full pl-12 pr-4 py-4 bg-gray-50 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 text-gray-900 placeholder-gray-500 ${
                            errors.email ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                          }`}
                          placeholder="john.doe@example.com"
                        />
                      </div>
                      {errors.email && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-sm text-red-600"
                        >
                          {errors.email}
                        </motion.p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Password *
                        </label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          className={`w-full px-4 py-4 bg-gray-50 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 text-gray-900 placeholder-gray-500 ${
                            errors.password ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                          }`}
                          placeholder="Minimum 6 characters"
                        />
                        {errors.password && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-2 text-sm text-red-600"
                          >
                            {errors.password}
                          </motion.p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Confirm Password *
                        </label>
                        <input
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                          className={`w-full px-4 py-4 bg-gray-50 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 text-gray-900 placeholder-gray-500 ${
                            errors.confirmPassword ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                          }`}
                          placeholder="Confirm your password"
                        />
                        {errors.confirmPassword && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-2 text-sm text-red-600"
                          >
                            {errors.confirmPassword}
                          </motion.p>
                        )}
                      </div>
                    </div>

                    {/* Trial Info */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl md:rounded-3xl p-4 md:p-6">
                      <div className="flex items-center mb-3 md:mb-4">
                        <ClockIcon className="h-5 w-5 md:h-6 md:w-6 text-green-600 mr-2 md:mr-3" />
                        <h3 className="text-base md:text-lg font-bold text-green-800">Free 2-Day Trial</h3>
                      </div>
                      <p className="text-green-700 mb-3 md:mb-4 text-sm md:text-base">
                        Your trial includes full access to all features. No credit card required.
                      </p>
                      <div className="flex flex-wrap gap-3 md:gap-4 text-xs md:text-sm text-green-700">
                        <div className="flex items-center">
                          <ShieldCheckIcon className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                          <span>Secure</span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircleIcon className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                          <span>No commitment</span>
                        </div>
                        <div className="flex items-center">
                          <SparklesIcon className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                          <span>Full features</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
            <div className="flex flex-col sm:flex-row justify-between gap-4 mt-6 md:mt-8">
              <motion.button
                whileHover={{ scale: currentStep === 1 ? 1 : 1.02 }}
                whileTap={{ scale: currentStep === 1 ? 1 : 0.98 }}
                onClick={handlePrev}
                disabled={currentStep === 1}
                className="flex items-center justify-center px-6 py-3 md:px-10 md:py-5 border-2 border-gray-200 text-gray-700 rounded-2xl md:rounded-3xl hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 transition-all duration-300 font-bold shadow-lg hover:shadow-xl backdrop-blur-sm text-sm md:text-base"
              >
                <ArrowLeftIcon className="h-4 w-4 md:h-5 md:w-5 mr-2 md:mr-3" />
                Previous
              </motion.button>

              {currentStep < 3 ? (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleNext}
                  className="flex items-center justify-center px-6 py-3 md:px-10 md:py-5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-2xl md:rounded-3xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 text-sm md:text-base"
                >
                  Next
                  <ArrowRightIcon className="h-4 w-4 md:h-5 md:w-5 ml-2 md:ml-3" />
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center justify-center px-8 py-3 md:px-12 md:py-5 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 text-white rounded-2xl md:rounded-3xl font-bold shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:from-green-600 hover:via-emerald-600 hover:to-teal-600 text-sm md:text-base"
                >
                  {loading ? (
                    <LoadingSpinner size="small" />
                  ) : (
                    <>
                      Start Free Trial
                      <SparklesIcon className="h-5 w-5 md:h-6 md:w-6 ml-2 md:ml-3" />
                    </>
                  )}
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Custom Field Modal */}
      {showCustomFieldModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Add Custom Field</h3>
                <button
                  onClick={() => setShowCustomFieldModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Name *
                  </label>
                  <input
                    type="text"
                    value={customFieldForm.name}
                    onChange={(e) => setCustomFieldForm({ ...customFieldForm, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Enter field name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Type *
                  </label>
                  <select 
                    value={customFieldForm.type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setCustomFieldForm({ 
                        ...customFieldForm, 
                        type: newType,
                        options: (newType === 'dropdown' || newType === 'multiselect') ? customFieldForm.options : []
                      });
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="date">Date</option>
                    <option value="dropdown">Dropdown</option>
                    <option value="textarea">Textarea</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="url">URL</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Module *
                  </label>
                  <select 
                    value={customFieldForm.module}
                    onChange={(e) => setCustomFieldForm({ ...customFieldForm, module: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="lead">Lead</option>
                    <option value="customer">Customer</option>
                  </select>
                </div>

                {(customFieldForm.type === 'dropdown' || customFieldForm.type === 'multiselect') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Options
                    </label>
                    <div className="space-y-3">
                      {/* Display existing options as chips */}
                      {customFieldForm.options && customFieldForm.options.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {customFieldForm.options.map((option, optIndex) => (
                            <span key={optIndex} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-emerald-100 text-emerald-800">
                              {option}
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedOptions = customFieldForm.options.filter((_, i) => i !== optIndex);
                                  setCustomFieldForm({ ...customFieldForm, options: updatedOptions });
                                }}
                                className="ml-2 text-emerald-600 hover:text-emerald-800 focus:outline-none"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Add new option input */}
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          placeholder="Add new option"
                          className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const value = e.target.value.trim();
                              if (value && !customFieldForm.options.includes(value)) {
                                setCustomFieldForm({
                                  ...customFieldForm,
                                  options: [...customFieldForm.options, value]
                                });
                                e.target.value = '';
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            const input = e.target.previousElementSibling;
                            const value = input.value.trim();
                            if (value && !customFieldForm.options.includes(value)) {
                              setCustomFieldForm({
                                ...customFieldForm,
                                options: [...customFieldForm.options, value]
                              });
                              input.value = '';
                            }
                          }}
                          className="px-3 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 text-sm font-medium"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={customFieldForm.required}
                    onChange={(e) => setCustomFieldForm({ ...customFieldForm, required: e.target.checked })}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    Required field
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setCustomFieldForm({
                      name: '',
                      type: 'text',
                      module: 'lead',
                      required: false,
                      options: []
                    });
                    setShowCustomFieldModal(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    if (customFieldForm.name.trim()) {
                      setFormData({
                        ...formData,
                        selectedCustomFields: [...(formData.selectedCustomFields || []), {
                          ...customFieldForm,
                          name: customFieldForm.name.trim(),
                          isIndustry: false
                        }]
                      });
                      setCustomFieldForm({
                        name: '',
                        type: 'text',
                        module: 'lead',
                        required: false,
                        options: []
                      });
                      setShowCustomFieldModal(false);
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  Add Field
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Onboarding;